'use client';
import { type VariantProps } from 'class-variance-authority';
import React, {
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';

import type { IconType } from '@/app/components/icons';
import { cn } from '@/app/utils/cn';
import { cnva } from '@/app/utils/cnva';
import {
  createDataAttributes,
  type ExtendDataKeys,
} from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { safeCall } from '@/app/utils/safeCall';

const TabItemStyled = cnva(
  'font-noto-bold transition-form relative min-h-12 text-center text-gray-400 focus-visible:outline-none',
  {
    variants: {
      theme: {
        light: '',
        dark: '',
      },
      variant: {
        filled: '',
        outer: 'rounded-2xs',
        border: [
          'text-body-2-fixed before:transition-form before:absolute before:inset-0 before:border-y before:border-r',
          'first:before:rounded-l-2xs last:before:rounded-r-2xs first:before:border-l',
        ],
        underline:
          'text-body-2 before:transition-form min-w-32 px-6 py-3 before:absolute before:inset-0 before:border-b-2 lg:py-4',
      },
      isSelected: {
        true: 'pointer-events-none',
        false: 'cursor-pointer',
      },
      hasIcon: {
        true: 'inline-flex items-center justify-center gap-x-2',
      },
      hasIndicator: {
        true: 'w-full',
        false: 'max-w-56 truncate',
      },
    },
    compoundVariants: [
      {
        variant: ['filled', 'outer', 'border'],
        theme: 'light',
        isSelected: false,
        className:
          'hover:bg-gray-50 hover:text-gray-700 focus-visible:bg-gray-50 focus-visible:text-gray-700',
      },
      {
        variant: ['filled', 'outer', 'border'],
        theme: 'dark',
        isSelected: false,
        className:
          'hover:bg-gray-700 hover:text-gray-50 focus-visible:bg-gray-700 focus-visible:text-gray-50',
      },
      {
        variant: ['filled', 'outer', 'border'],
        className: 'px-6 py-3',
      },
      {
        variant: ['filled', 'outer', 'border'],
        theme: 'light',
        isSelected: true,
        className: 'bg-gray-800 text-white',
      },
      {
        variant: ['filled', 'outer', 'border'],
        theme: 'dark',
        isSelected: true,
        className: 'bg-white text-gray-900',
      },
      {
        variant: ['filled', 'outer'],
        className: 'text-button-1',
      },
      {
        variant: 'filled',
        theme: 'light',
        isSelected: false,
        hasIndicator: false,
        className: 'bg-white',
      },
      {
        variant: 'filled',
        theme: 'dark',
        isSelected: false,
        hasIndicator: false,
        className: 'bg-gray-800',
      },
      {
        variant: 'border',
        theme: 'light',
        className: 'before:border-gray-800',
      },
      {
        variant: 'border',
        theme: 'dark',
        className: 'before:border-white',
      },
      {
        variant: 'underline',
        theme: 'light',
        className:
          'before:border-gray-200 hover:text-gray-700 hover:before:border-gray-700',
      },
      {
        variant: 'underline',
        theme: 'dark',
        className:
          'before:border-gray-500 hover:text-gray-600 hover:before:border-gray-700',
      },
      {
        variant: 'underline',
        theme: 'light',
        isSelected: true,
        className:
          'text-gray-900 before:border-gray-900 hover:text-gray-900 hover:before:border-gray-900',
      },
      {
        variant: 'underline',
        theme: 'dark',
        isSelected: true,
        className:
          'text-white before:border-white hover:text-white hover:before:border-white',
      },
    ],
    defaultVariants: {
      theme: 'light',
      variant: 'filled',
    },
  }
);

export interface TabItem {
  label: string;
  value: string;
  icon?: IconType;
}

export interface TabsProps {
  /**
   * (Optional) ref for the container element
   */
  ref?: RefObject<HTMLDivElement | null>;

  /**
   * (Optional) The classname of the wrapper of the tabs.
   */
  wrapperClassName?: string;

  /**
   * (Optional) The classname of the tab items.
   */
  className?: string;

  /**
   * (Optional) ref for the items container element
   */
  itemsRef?: RefObject<HTMLButtonElement[]>;

  /**
   * (Optional) The theme of the tabs.
   */
  theme?: VariantProps<typeof TabItemStyled>['theme'];

  /**
   * (Optional) The variant of the tabs.
   */
  variant?: VariantProps<typeof TabItemStyled>['variant'];

  /**
   * The items of the tabs.
   * Each item should contain a `label` and a `value`.
   */
  items: TabItem[];

  /**
   * (Optional) The selected tab value, if not provided, the first tab will be selected.
   */
  value?: string;

  /**
   * (Optional) The callback function when a tab is changed.
   */
  onChange?: (value: string, index: number, item: TabItem) => void;

  /**
   * (Optional) The aria-label of the tabs for accessibility.
   */
  'aria-label'?: string;

  /**
   * (Optional) Data for GA tracking
   */
  tracking?: {
    eventName: string;
    group: string;
  };

  /**
   * (Optional) Force hide the tabs when items length is less than or equal to 1.
   */
  forceHide?: boolean;

  /**
   * (Optional) The component to render as the indicator.
   */
  indicatorComponent?: ReactNode;

  /**
   * (Optional) Disable overflow scroll wrapper
   */
  disableOverflow?: boolean;
}

type TabDataKeys = ExtendDataKeys<'selected'>;

export const Tabs = ({
  ref,
  wrapperClassName = '',
  className = '',
  itemsRef,
  theme = 'light',
  variant = 'filled',
  items = [],
  value,
  onChange,
  'aria-label': ariaLabel,
  tracking,
  forceHide = true,
  indicatorComponent,
  disableOverflow = false,
  ...props
}: TabsProps) => {
  const [selectedTab, setSelectedTab] = useState<string>(
    value || items?.[0]?.value
  );
  const tabRefs = useRef<HTMLButtonElement[] | null>([]);

  const handleOnTabChange = (tab: TabItem, index: number) => {
    if (tab?.value === selectedTab) return;

    setSelectedTab(tab.value);
    safeCall(onChange, tab?.value, index, tab);
  };

  useEffect(() => {
    if (!value) return;
    setSelectedTab(value);
  }, [value]);

  // Handle keyboard navigation for accessibility
  const handleOnKeyDown =
    (index: number) => (event: KeyboardEvent<HTMLButtonElement>) => {
      if (!tabRefs.current) return;

      if (['ArrowRight', 'ArrowLeft'].includes(event.nativeEvent.code)) {
        event.preventDefault();

        const elements = tabRefs.current.filter(
          (ref): ref is HTMLButtonElement => !!ref
        );

        if (elements.length === 0) return;

        const nextIndex = (index + 1) % elements.length;
        const prevIndex = (index - 1 + elements.length) % elements.length;

        if (event.nativeEvent.code === 'ArrowRight') {
          elements[nextIndex].focus();
        }

        if (event.nativeEvent.code === 'ArrowLeft') {
          elements[prevIndex].focus();
        }
      }
    };

  useEffect(() => {
    if (!tabRefs.current || !itemsRef || itemsRef.current.length > 0) {
      return;
    }

    itemsRef.current = tabRefs.current;
  }, [itemsRef]);

  if (forceHide && items.length <= 1) return null;

  return (
    <div
      className={cn({ 'scrollbar-hidden overflow-x-auto': !disableOverflow })}
    >
      <div
        ref={ref}
        className={cn(
          'relative flex',
          {
            'w-max': !indicatorComponent,
            'p-1.5 before:absolute before:inset-0 before:rounded-xs before:border':
              variant === 'outer',
            'rounded-2xs overflow-hidden':
              variant === 'filled' || variant === 'border',
            'before:border-gray-800': variant === 'outer' && theme === 'light',
            'before:border-white': variant === 'outer' && theme === 'dark',
          },
          wrapperClassName
        )}
        role="tablist"
        aria-label={ariaLabel || 'Tabs'}
        aria-orientation="horizontal"
        {...createDataAttributes({
          test: 'tabs',
        })}
        {...props}
      >
        {items.map((tab, index) => {
          const isSelected = selectedTab === tab.value;
          const hasIcon = !!tab.icon;
          const showIndicator = isSelected && !!indicatorComponent;

          return (
            <button
              key={tab.value}
              ref={(ref) => {
                if (!tabRefs.current) return;
                tabRefs.current[index] = ref as HTMLButtonElement;
              }}
              role="tab"
              type="button"
              tabIndex={isSelected ? 0 : -1}
              onClick={() => handleOnTabChange(tab, index)}
              onKeyDown={handleOnKeyDown(index)}
              className={TabItemStyled({
                theme,
                variant,
                hasIcon,
                hasIndicator: !!indicatorComponent,
                isSelected: isSelected && !indicatorComponent,
                className,
              })}
              aria-controls={`tabpanel-${tab.value}`}
              aria-selected={isSelected}
              {...createDataAttributes<TabDataKeys>({
                test: `tab_${index}`,
                selected: isSelected ? 'true' : null,
              })}
              {...(tracking
                ? createDataGA({
                    click: true,
                    eventName: tracking.eventName,
                    group: tracking.group,
                    text: tab?.label || '',
                    value: tab?.value || '',
                  })
                : {})}
            >
              {!!hasIcon || indicatorComponent ? (
                <>
                  {tab.icon}
                  {!!showIndicator && indicatorComponent}
                  <span
                    className={cn('truncate', {
                      relative: !!indicatorComponent,
                    })}
                  >
                    {tab.label}
                  </span>
                </>
              ) : (
                tab.label
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
